import { atomWithStorage } from "jotai/utils";
import { safeJSONStorage } from "@/store/safeStorage";
import type { BlockType } from "@/components/Layouts/blockTypes";

export type OpenBlock = {
  type: BlockType;
  id: string;
  name: string;
  // The block's own picked icon (a WORKFLOW_ICONS key, a "spot:" upload or a
  // URL), what NoxusIcon takes.
  icon?: string;
  // Where it was opened from, query and all: a flow reached from an inbox's
  // automations carries that inbox, and going back to it from the sidebar has
  // to land in the same place, not on the bare flow.
  href?: string;
  // When it was last opened. The list is capped, so this is what decides
  // which one falls off; blocks stored before the cap carry none and go first.
  visitedAt?: number;
};

const storage = safeJSONStorage<Record<string, OpenBlock[]>>(
  "__open_blocks_probe__"
);

// Open blocks are per workspace: switching workspaces shows that workspace's
// own set, and blocks from one never leak into another's sidebar.
export const OPEN_BLOCKS_STORAGE_KEY = "open-blocks-by-workspace";

export const openBlocksByGroupAtom = atomWithStorage<
  Record<string, OpenBlock[]>
>(OPEN_BLOCKS_STORAGE_KEY, {}, storage);

// The sidebar shows six rows: past that the sections below start being pushed
// off a short window. The rest stay open behind a "more" row.
export const VISIBLE_OPEN_BLOCKS = 6;

// What is kept at all: past this the one visited longest ago is forgotten.
export const MAX_OPEN_BLOCKS = 50;

const recency = (block: OpenBlock) => block.visitedAt ?? 0;

// Ties keep the later row, so a list stored before the cap existed (no
// visits on any of it) trims to the ones opened most recently.
const capped = (blocks: OpenBlock[], keep: string): OpenBlock[] => {
  if (blocks.length <= MAX_OPEN_BLOCKS) return blocks;
  const kept = new Set(
    blocks
      .map((block, index) => ({ block, index }))
      .sort(
        (a, b) =>
          Number(b.block.id === keep) - Number(a.block.id === keep) ||
          recency(b.block) - recency(a.block) ||
          b.index - a.index
      )
      .slice(0, MAX_OPEN_BLOCKS)
      .map((entry) => entry.block.id)
  );
  return blocks.filter((block) => kept.has(block.id));
};

export const openBlock = (
  blocks: OpenBlock[],
  block: OpenBlock,
  now: number = Date.now()
): OpenBlock[] => {
  const index = blocks.findIndex((b) => b.id === block.id);
  const visited = { ...block, visitedAt: now };
  // A tool new to the list (just created, duplicated or opened) goes on top.
  if (index < 0) return capped([visited, ...blocks], block.id);
  // Opened from behind "more": it takes the last visible row for good, and
  // the row it replaces moves behind "more" in its place.
  if (index >= VISIBLE_OPEN_BLOCKS) {
    const rest = blocks.filter((_, i) => i !== index);
    return [
      ...rest.slice(0, VISIBLE_OPEN_BLOCKS - 1),
      visited,
      ...rest.slice(VISIBLE_OPEN_BLOCKS - 1),
    ];
  }
  const open = blocks[index];
  if (
    open.name === block.name &&
    open.type === block.type &&
    open.icon === block.icon &&
    open.href === block.href &&
    // Only skip the write when this block is already alone at the front of
    // the queue: a tie leaves every visit unrecorded, which is the state a
    // list stored before the cap starts in.
    blocks.every((b, i) => i === index || recency(b) < recency(open))
  ) {
    return blocks;
  }
  return blocks.map((b, i) => (i === index ? visited : b));
};

export const closeBlock = (blocks: OpenBlock[], id: string): OpenBlock[] =>
  blocks.filter((b) => b.id !== id);

// Where to land when the block you are looking at is the one being closed.
export const neighbourAfterClose = (
  blocks: OpenBlock[],
  id: string
): OpenBlock | null => {
  const index = blocks.findIndex((b) => b.id === id);
  if (index < 0) return null;
  return blocks[index - 1] ?? blocks[index + 1] ?? null;
};

// Where to land when the block on screen was deleted: the nearest open block
// of its kind, so deleting a flow never drops the user on an unrelated agent.
export const sameKindAfterDelete = (
  blocks: OpenBlock[],
  id: string
): OpenBlock | null => {
  const deleted = blocks.find((b) => b.id === id);
  if (!deleted) return null;
  return neighbourAfterClose(
    blocks.filter((b) => b.type === deleted.type),
    id
  );
};

// The rows the sidebar shows, in list order, and the ones behind "more". The
// block on screen is always shown, taking the last visible slot if the list
// order would hide it. `slots` is fewer while a row is held for a block not
// listed yet.
export const splitVisible = (
  blocks: OpenBlock[],
  currentId: string,
  slots: number = VISIBLE_OPEN_BLOCKS
): { visible: OpenBlock[]; hidden: OpenBlock[] } => {
  if (blocks.length <= slots) return { visible: blocks, hidden: [] };
  const head = blocks.slice(0, slots);
  const current = blocks.find((b) => b.id === currentId);
  const visible =
    !current || head.includes(current)
      ? head
      : [...head.slice(0, slots - 1), current];
  const shown = new Set(visible.map((b) => b.id));
  return { visible, hidden: blocks.filter((b) => !shown.has(b.id)) };
};

// Dragging a row onto another moves it among the rows shown, and that order
// is the list's from then on, the tool on screen included where it was shown.
export const moveVisible = (
  blocks: OpenBlock[],
  currentId: string,
  activeId: string,
  overId: string
): OpenBlock[] => {
  const { visible, hidden } = splitVisible(blocks, currentId);
  const from = visible.findIndex((b) => b.id === activeId);
  const to = visible.findIndex((b) => b.id === overId);
  if (from < 0 || to < 0 || from === to) return blocks;
  const moved = visible.filter((_, i) => i !== from);
  moved.splice(to, 0, visible[from]);
  return [...moved, ...hidden];
};
